import { useState } from 'react';
import { useAppDispatch } from 'redux/hooks';
import { useNavigate } from 'react-router-dom';
import { Alert } from 'lib/components/Alert';

import TemporaryPasswordResetContaner from 'layouts/signInLayout/TemporaryPasswordResetContainer';
import { logAndCaptureException } from 'utils';
import { loginUser } from 'utils/users';
import { getRedirect } from 'utils/urls';
import { logout } from 'redux/auth/authActions';
import { apiPost } from 'api/typed';
import { ColumnService } from 'lib/services/directory';
import { ColumnH1 } from 'lib/components/Typography';
import LoginUserForm, { DEFAULT_LOGIN_DATA } from './LoginUserForm';
import LoginUserFooter from './LoginUserFooter';
import SocialLoginsForm from './SocialLoginsForm';

export default function Login() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState(DEFAULT_LOGIN_DATA);
  const [showTemporaryPasswordAlert, setShowTemporaryPasswordAlert] =
    useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(
    null
  );

  const login = async () => {
    setLoggingIn(true);
    setError(null);
    const redirect = getRedirect();
    const { error: logoutError } = await dispatch(logout());

    if (logoutError) {
      setLoggingIn(false);
      return setError({
        title: 'Error logging in',
        message: 'Please try again.'
      });
    }

    const [success, authError] = await loginUser(
      loginData.email,
      loginData.password
    );

    if (success) {
      navigate(redirect);
    } else {
      if (authError.code === 'auth/wrong-password') {
        try {
          const res = await apiPost('users/get-user-by-email', {
            email: loginData.email
          });

          if (res.success && res.data.hasTemporaryPassword) {
            setShowTemporaryPasswordAlert(true);
            setLoggingIn(false);
            return;
          }
        } catch (err) {
          logAndCaptureException(
            ColumnService.AUTH_AND_USER_MANAGEMENT,
            err,
            'Error getting user by email',
            {
              email: loginData.email
            }
          );
        }
      }
      setError({
        title: 'Error logging in',
        message: authError.message
      });
      setLoggingIn(false);
    }
  };

  return (
    <div className="w-full space-y-2">
      <ColumnH1 className="text-center">Log in to your account</ColumnH1>
      {error && (
        <Alert
          status="error"
          id={'login-error'}
          title={error.title}
          description={error.message}
        />
      )}
      <LoginUserForm
        onChangeLoginData={setLoginData}
        loginData={loginData}
        loggingIn={loggingIn}
        onSubmit={login}
      />
      <SocialLoginsForm />
      {showTemporaryPasswordAlert && (
        <TemporaryPasswordResetContaner email={loginData.email} />
      )}
      <LoginUserFooter />
    </div>
  );
}
